/**
 * Cognito-backed auth for the SPA.
 *
 * Wraps `amazon-cognito-identity-js` (which persists the session in
 * localStorage) behind the small promise-based {@link AuthContextValue}. Sign-in
 * uses SRP. The SPA always sends the Cognito ID token to the API (never the
 * access token): the API's JWT authorizer pins `aud` to the SPA client id, which
 * only ID tokens carry, and only ID tokens carry the `custom:tenantId` claim the
 * backend derives the tenant from. See SECURITY.md.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { config } from '../config';
import { AuthContext, type AuthContextValue, type AuthStatus, type AuthUser } from './context';
import { AuthError, cognitoCode } from './errors';

function newUserPool(): CognitoUserPool {
  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.clientId,
  });
}

/** Turn a Cognito error (unknown shape) into a readable message. */
function toMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) {
      return message;
    }
  }
  return fallback;
}

/** Wrap a Cognito error so the friendly-copy layer can key off its original code. */
function authError(error: unknown, fallback: string): AuthError {
  return new AuthError(toMessage(error, fallback), cognitoCode(error));
}

export interface AuthProviderProps {
  readonly children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const poolRef = useRef<CognitoUserPool | null>(null);
  const pool = (poolRef.current ??= newUserPool());

  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);

  const applySession = useCallback((email: string, session: CognitoUserSession): void => {
    setIdToken(session.getIdToken().getJwtToken());
    setUser({ email });
    setStatus('signedIn');
  }, []);

  const clear = useCallback((): void => {
    setIdToken(null);
    setUser(null);
    setStatus('signedOut');
  }, []);

  // Restore any persisted session on mount.
  useEffect(() => {
    const current = pool.getCurrentUser();
    if (current === null) {
      setStatus('signedOut');
      return;
    }
    current.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error !== null || session === null) {
        clear();
        return;
      }
      if (!session.isValid()) {
        clear();
        return;
      }
      // Restore path: `getUsername()` is the Cognito username, which for this pool is
      // the immutable sub (a GUID), so the account menu would otherwise show a GUID
      // after a reload. Derive the display email from the ID token's `email` claim
      // instead, falling back to the username only if the claim is missing.
      const claims = session.getIdToken().decodePayload() as Record<string, unknown>;
      const claimEmail = claims.email;
      const email =
        typeof claimEmail === 'string' && claimEmail.length > 0 ? claimEmail : current.getUsername();
      applySession(email, session);
    });
  }, [pool, applySession, clear]);

  const signUp = useCallback(
    (email: string, password: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const attributes = [new CognitoUserAttribute({ Name: 'email', Value: email })];
        pool.signUp(email, password, attributes, [], (error) => {
          if (error) {
            reject(authError(error, 'Could not create the account.'));
            return;
          }
          resolve();
        });
      }),
    [pool],
  );

  const confirm = useCallback(
    (email: string, code: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
        cognitoUser.confirmRegistration(code, true, (error) => {
          if (error) {
            reject(authError(error, 'Could not confirm the code.'));
            return;
          }
          resolve();
        });
      }),
    [pool],
  );

  const resendCode = useCallback(
    (email: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
        cognitoUser.resendConfirmationCode((error) => {
          if (error) {
            reject(authError(error, 'Could not resend the code.'));
            return;
          }
          resolve();
        });
      }),
    [pool],
  );

  const signIn = useCallback(
    (email: string, password: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
        const details = new AuthenticationDetails({ Username: email, Password: password });
        cognitoUser.authenticateUser(details, {
          onSuccess: (session) => {
            applySession(email, session);
            resolve();
          },
          onFailure: (error) => {
            reject(authError(error, 'Could not sign in.'));
          },
        });
      }),
    [pool, applySession],
  );

  const forgotPassword = useCallback(
    (email: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
        cognitoUser.forgotPassword({
          onSuccess: () => resolve(),
          onFailure: (error) => reject(authError(error, 'Could not start the password reset.')),
        });
      }),
    [pool],
  );

  const confirmForgotPassword = useCallback(
    (email: string, code: string, newPassword: string): Promise<void> =>
      new Promise((resolve, reject) => {
        const cognitoUser = new CognitoUser({ Username: email, Pool: pool });
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: (error) => reject(authError(error, 'Could not reset the password.')),
        });
      }),
    [pool],
  );

  const signOut = useCallback((): void => {
    const current = pool.getCurrentUser();
    if (current !== null) {
      current.signOut();
    }
    clear();
  }, [pool, clear]);

  const getIdToken = useCallback(
    (): Promise<string> =>
      new Promise((resolve, reject) => {
        const current = pool.getCurrentUser();
        if (current === null) {
          reject(new Error('Not signed in.'));
          return;
        }
        // getSession refreshes the tokens with the refresh token if they expired.
        current.getSession((error: Error | null, session: CognitoUserSession | null) => {
          if (error !== null || session === null) {
            reject(new Error('Session expired.'));
            return;
          }
          if (!session.isValid()) {
            reject(new Error('Session expired.'));
            return;
          }
          const token = session.getIdToken().getJwtToken();
          setIdToken(token);
          resolve(token);
        });
      }),
    [pool],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      idToken,
      signUp,
      confirm,
      resendCode,
      signIn,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      getIdToken,
    }),
    [
      status,
      user,
      idToken,
      signUp,
      confirm,
      resendCode,
      signIn,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      getIdToken,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
